import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../ui/UIElements/Icon";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "../../ui/UIElements/HoverCard";
import SettingsConfig from "./SettingsConfig";

const SettingsCategory = ({ category, isLoading, error }) => {
  const { t } = useTranslation(["settings", "common"]);
  const [isExpanded, setIsExpanded] = useState(true);
  const navigate = useNavigate();

  const getCategoryIcon = (key) => {
    switch (key) {
      case "platform_currency_settings":
        return "DollarSign";
      case "platform_distance_measurement_settings":
        return "MapPin";
      case "platform_date_time_formats":
        return "Clock";
      case "basic_order_types":
        return "ShoppingCart";
      case "pro_preorder":
        return "Package";
      default:
        return "Settings";
    }
  };

  const getCategoryColor = (key) => {
    switch (key) {
      case "platform_currency_settings":
        return "text-green-400";
      case "platform_distance_measurement_settings":
        return "text-blue-400";
      case "platform_date_time_formats":
        return "text-purple-400";
      case "basic_order_types":
        return "text-orange-400";
      case "pro_preorder":
        return "text-cyan-400";
      default:
        return "text-gray-400";
    }
  };

  if (error) {
    return (
      <div className="rounded-lg border border-gray-700 bg-gray-800 p-4">
        <div className="flex items-center space-x-2 text-red-400">
          <Icon name="AlertCircle" className="h-5 w-5" />
          <span className="text-sm">{t("category.errors.loadFailed")}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-700 bg-gray-800">
      {/* Category Header */}
      <div
        className="hover:bg-gray-750 cursor-pointer p-4 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div
              className={`rounded-lg bg-gray-700 p-2 ${getCategoryColor(category.key)}`}
            >
              <Icon name={getCategoryIcon(category.key)} className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-white">
                {category.name}
              </h3>
              {category.description && (
                <p className="mt-1 text-sm text-gray-400">
                  {category.description}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <HoverCard>
              <HoverCardTrigger asChild>
                <button
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent category expansion
                    navigate("/support");
                  }}
                  className="p-1 text-gray-400 transition-colors hover:text-blue-400"
                  title={t("category.actions.helpTitle")}
                >
                  <Icon name="HelpCircle" className="h-4 w-4" />
                </button>
              </HoverCardTrigger>
              <HoverCardContent side="left" className="max-w-xs">
                <div className="text-center">
                  <p className="mb-2 text-sm">{t("category.helpPrompt")}</p>
                  <button
                    onClick={() => navigate("/support")}
                    className="rounded bg-blue-600 px-3 py-1 text-xs text-white transition-colors hover:bg-blue-700"
                  >
                    {t("category.actions.goToSupport")}
                  </button>
                </div>
              </HoverCardContent>
            </HoverCard>
            <Icon
              name="ChevronDown"
              className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${
                isExpanded ? "rotate-180" : ""
              }`}
            />
          </div>
        </div>
      </div>

      {/* Category Content */}
      {isExpanded && (
        <div className="border-t border-gray-700">
          {isLoading ? (
            <div className="space-y-4 p-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="rounded-lg border border-gray-700 bg-gray-800 p-4"
                >
                  <div className="mb-3 flex items-start justify-between">
                    <div className="flex-1">
                      <div className="mb-1 flex items-center space-x-2">
                        <div className="h-4 w-32 animate-pulse rounded bg-gray-700"></div>
                      </div>
                      <div className="mb-2 h-3 w-48 animate-pulse rounded bg-gray-700"></div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="h-6 w-16 animate-pulse rounded bg-gray-700"></div>
                      <div className="h-6 w-12 animate-pulse rounded bg-gray-700"></div>
                    </div>
                  </div>
                  <div className="h-9 animate-pulse rounded bg-gray-700"></div>
                </div>
              ))}
            </div>
          ) : category?.configs && category?.configs.length > 0 ? (
            <div className="space-y-4 p-4">
              {category?.configs.map((config) => (
                <SettingsConfig key={config.id} config={config} />
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-400">
              <Icon
                name="Settings"
                className="mx-auto mb-2 h-8 w-8 opacity-50"
              />
              <p className="text-sm">{t("category.empty")}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SettingsCategory;
